import {
  FormControl,
  Autocomplete,
  TextField,
  CircularProgress,
  TextFieldProps,
  Button,
  Paper,
} from '@mui/material';
import { ConfigEntryType } from 'config/config';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import AddLineIcon from 'remixicon-react/AddLineIcon';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import { useNavigate } from 'react-router-dom';
import { FocusEvent, useState, KeyboardEvent, useEffect } from 'react';
import { ADD_PROJECT } from 'constants/routes';
import { useConfigEntryOptionsQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';

type PostSelectCustomFieldProps = {
  autoFocus?: boolean;
  field: ConfigEntryType;
  TextFieldProps?: TextFieldProps;
  showAddProject?: boolean;
  showLabel?: boolean;
};

type OptionType = { label: string; value: string };

function PostSelectCustomField({
  autoFocus,
  field,
  TextFieldProps,
  showAddProject,
  showLabel,
}: PostSelectCustomFieldProps) {
  const navigate = useNavigate();
  const { singularProjectsModuleName } = useBuildWiseModules();
  const [inputValue, setInputValue] = useState('');
  const {
    formState: { isSubmitting },
    getValues,
    setValue,
    control,
  } = useFormContext();
  const [open, setOpen] = useState(false);
  const value = useWatch({ control, name: field?.entryCode });
  const { companyId } = useUserData();
  const { activeUniqueOrderedOptions, isLoading } = useConfigEntryOptionsQuery({
    companyId,
    configEntry: field,
    defaultOptions: typeof value === 'string' && value.trim() ? [{ label: value, value }] : [],
  });
  const [fieldValue, setFieldValue] = useState<OptionType | null>(
    typeof value === 'string' && value.trim()
      ? activeUniqueOrderedOptions?.find((option) => option.value === value) || {
          label: value,
          value,
        }
      : null,
  );

  const handleSelect = (_: unknown, values: OptionType | null | string) => {
    const selectedValue = typeof values === 'string' ? values : values?.value;
    setValue(field.entryCode, selectedValue || '', { shouldDirty: true, shouldValidate: true });
    setFieldValue(typeof values === 'string' ? { label: values, value: values } : values);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown') {
      setOpen(true);
    }
  };

  useEffect(() => {
    if (activeUniqueOrderedOptions?.length) {
      setFieldValue(activeUniqueOrderedOptions?.find((option) => option.value === value) || null);
    }
  }, [activeUniqueOrderedOptions]);

  // Updating the fieldvalue because in retro form we are setting the default value in the parent using setValue
  // Default value won't be updated if we remove this
  useEffect(() => {
    if (value) {
      setFieldValue(
        typeof value === 'string'
          ? activeUniqueOrderedOptions?.find((option) => option.value === value) || {
              label: value,
              value: value,
            }
          : value,
      );
    } else {
      setFieldValue(null);
    }
  }, [value]);

  return (
    <>
      <Controller
        control={control}
        render={({ fieldState, field: { ref } }) => (
          <FormControl fullWidth sx={showLabel ? { my: 1 } : {}}>
            <Autocomplete
              id={field.entryCode}
              {...(field?.referenceObject?.freeText && {
                freeSolo: true,
                open,
                onOpen: (event) => {
                  const currentValue = (event.target as HTMLInputElement).value;
                  if (currentValue?.trim()) {
                    setOpen(true);
                  }
                },
                onClose: () => setOpen(false),
                onKeyDown: handleKeyDown,
              })}
              onChange={handleSelect}
              options={activeUniqueOrderedOptions}
              autoHighlight
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              onInputChange={(_, value) => {
                setInputValue(value);
                if (!!field?.referenceObject?.freeText) {
                  if (!value && open) {
                    setOpen(false);
                  }
                  // Apply custom input value
                  setValue(field.entryCode, value, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }
              }}
              value={fieldValue}
              noOptionsText={isLoading ? 'Loading...' : 'No Data Found'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus={autoFocus}
                  inputRef={ref}
                  variant="standard"
                  error={!!fieldState.error}
                  helperText={fieldState?.error?.message || ''}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  {...(showLabel && {
                    label: field.referenceObject?.label || field.entryCode,
                  })}
                  {...(!showLabel && {
                    placeholder: field.referenceObject?.placeHolder || 'Enter value',
                    InputLabelProps: { shrink: true },
                  })}
                  {...TextFieldProps}
                />
              )}
              disabled={isSubmitting}
              onBlur={(event: FocusEvent) => {
                if (
                  event.relatedTarget?.id === 'add-project' &&
                  showAddProject &&
                  field.entryCode === 'projectId'
                ) {
                  event.stopPropagation();
                  navigate(ADD_PROJECT, {
                    state: {
                      articleFormState: getValues(),
                      projectName: inputValue,
                    },
                  });
                }
              }}
              {...(field.entryCode === 'projectId' &&
                showAddProject && {
                  PaperComponent: ({ children, ...rest }) => (
                    <Paper {...rest}>
                      {children}
                      <Button
                        id="add-project"
                        startIcon={<AddLineIcon size={20} />}
                        sx={(theme) => ({ color: theme.palette.custom.link, m: 1 })}
                      >
                        Add {singularProjectsModuleName}
                      </Button>
                    </Paper>
                  ),
                })}
            />
          </FormControl>
        )}
        name={field.entryCode}
      />
    </>
  );
}

export default PostSelectCustomField;
